import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import storage from 'redux-persist/lib/storage'
import apiClient from '../../../services/api-client'
import { RootState } from '../../../store/rootReducer'
import {
  StoresApiResponse,
  StoreInfo,
  StoresState,
  StoreHoursApiResponse,
  StoreHour,
} from './stores.types'

export const getStores = createAsyncThunk<StoreInfo[], void>('stores/getStores', async () => {
  const request = await apiClient.get<StoresApiResponse, AxiosError>(`/stores`)
  return request.data.stores
})

export const getStoreHours = createAsyncThunk<StoreHour[], string>(
  'stores/getStoreHours',
  async (inventorySiteId) => {
    const request = await apiClient.get<StoreHoursApiResponse, AxiosError>(
      `/stores/${inventorySiteId}/hours`,
    )
    return request.data.storeHours
  },
)

export const storesPersistConfig = {
  key: 'stores',
  storage: storage,
  blacklist: ['status'],
}

export const initialState: StoresState = {
  stores: [],
  status: 'idle',
}

const StoresSlice = createSlice({
  name: 'stores',
  initialState: initialState,
  extraReducers: (builder) => {
    builder.addCase(getStores.pending, (state) => {
      state.status = 'pending'
    })
    builder.addCase(getStores.fulfilled, (state, { payload }) => {
      state.status = 'fulfilled'
      state.stores = [...payload].sort((a, b) => a.orgUnitName.localeCompare(b.orgUnitName))
    })
    builder.addCase(getStores.rejected, (state) => {
      state.status = 'idle'
    })
    builder.addCase(getStoreHours.fulfilled, (state, { payload, meta }) => {
      const index = state.stores.findIndex((x) => x.inventorySiteId === meta.arg)
      state.stores[index].storeHours = payload
    })
  },
  reducers: {},
})

const selectState = (state: RootState): StoresState => state.stores

export const selectStores = createSelector(
  selectState,
  (state: StoresState): StoreInfo[] => state.stores,
)
export const selectPickupStores = createSelector(selectState, (state: StoresState): StoreInfo[] => {
  return state.stores.filter((s) => s.isPickupLocation)
})

export const selectStoreById = (id: string | undefined) => {
  return createSelector(selectState, (state) => {
    const store = state.stores.find((i) => i.inventorySiteId === id || i.inventoryLocationId === id)
    return store
  })
}

export type { StoresState } from './stores.types'
export default StoresSlice
